/**
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { ChatBubbleIcon, FileTextIcon } from "@radix-ui/react-icons";
import { Code } from "lucide-react";
// import { SiJira } from "react-icons/si";
import { useTheme } from "../Theme/ThemeProvider";
import { setRagChatType, clearRagHistory } from "../../store/chatModeSlice";
import { clearTranslationData, clearUploadedFiles } from "../../store/chatSlice";
import { RagChatType } from "../../store/chatModeSlice";

interface RagItem {
  key: RagChatType;
  label: string;
  subtitle: string;
  icon: React.ReactNode;
}

// Separate component so useState hover is isolated per-button and
// never conflicts with Framer Motion's inline style writes on the wrapper.
function RagButton({ item, index, accentHex, textColor, secondaryColor, isDark, onSelect, wrapperClassName }: {
  item: RagItem;
  index: number;
  accentHex: string;
  textColor: string;
  secondaryColor: string;
  isDark: boolean;
  onSelect: (key: RagChatType) => void;
  wrapperClassName?: string;
}) {
  const [hovered, setHovered] = useState(false);

  // Light mode: white card with subtle gray shadow; Dark mode: translucent white overlay
  const bgDefault = isDark
    ? "radial-gradient(circle at center, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%)"
    : "linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)";
  const bgHovered = isDark
    ? "radial-gradient(circle at center, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.05) 100%)"
    : "linear-gradient(145deg, #ffffff 0%, #e9ebee 100%)";
  const shadowDefault = isDark
    ? "0 1px 2px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.1)"
    : "0 2px 6px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9)";
  const shadowHovered = isDark
    ? `0 4px 12px rgba(0,0,0,0.3), inset 0 0 12px rgba(255,255,255,0.1)`
    : `0 6px 18px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,1)`;

  // Border gradient: dark uses white shimmer; light only shows border on hover
  const borderDefault = isDark
    ? "linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.25) 20%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.25) 80%, rgba(255,255,255,0.6) 100%)"
    : "none";
  const borderHovered = isDark
    ? "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.5) 20%, rgba(255,255,255,0.1) 40%, rgba(255,255,255,0.1) 60%, rgba(255,255,255,0.5) 80%, rgba(255,255,255,0.9) 100%)"
    : "linear-gradient(135deg, rgba(17,24,39,0.7) 0%, rgba(17,24,39,0.35) 20%, rgba(17,24,39,0.1) 40%, rgba(17,24,39,0.1) 60%, rgba(17,24,39,0.35) 80%, rgba(17,24,39,0.7) 100%)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.05, ease: "easeOut" }}
      className={`w-full h-full${wrapperClassName ? ` ${wrapperClassName}` : ''}`}
    >
      <button
        onClick={() => onSelect(item.key)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="relative overflow-visible rounded-2xl w-full h-full py-4 pl-4 pr-6 flex flex-row items-start gap-4 group cursor-pointer active:scale-95"
        style={{
          background: hovered ? bgHovered : bgDefault,
          boxShadow: hovered ? shadowHovered : shadowDefault,
          transform: hovered ? "scale(1.05) translateY(-4px)" : "scale(1) translateY(0px)",
          transition: "transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease",
        }}
      >
        {/* Gradient border — same mask trick as menu */}
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: hovered ? borderHovered : borderDefault,
            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            maskComposite: "xor",
            WebkitMaskComposite: "xor",
            padding: "1.5px",
            zIndex: -1,
          }}
        />
        {/* Icon — left aligned */}
        <div className="flex-shrink-0 transition-all group-hover:scale-110">
          {item.icon}
        </div>
        {/* Title + subtitle stacked */}
        <div className="flex flex-col items-start text-left">
          <p
            className="font-bold whitespace-nowrap"
            style={{ fontFamily: "3MCircularTT", color: textColor, fontSize: "clamp(0.85rem, 1.1vw, 1rem)" }}
          >
            {item.label}
          </p>
          <p
            className="line-clamp-4 break-words leading-tight text-xs"
            style={{ fontFamily: "3MCircularTT", color: secondaryColor }}
          >
            {item.subtitle}
          </p>
        </div>
      </button>
    </motion.div>
  );
}

export function KnowledgeRagSelector({ sharedPaddingClass }: { sharedPaddingClass: string }) {
  const dispatch = useDispatch();
  const { accent, mode } = useTheme();

  // Determine the accent hex for button borders/glows, matching the menu logic
  const getAccentHex = (a: string) => {
    if (a === "purple") return "#8b5cf6";
    if (a === "green") return "#10b981";
    if (a === "blue") return "#3b82f6";
    return "#e5e7eb";
  };

  const getLightAccentTextColor = (a: string) => {
    if (a === "purple") return "#a78bfa";
    if (a === "blue") return "#60a5fa";
    if (a === "green") return "#34d399";
    return "#e5e7eb";
  };

  const accentHex = getAccentHex(accent);
  // In dark mode use the light accent colour for visual pop; in light mode use the standard text colour so it's readable.
  const textColor = mode === 'dark' ? getLightAccentTextColor(accent) : 'var(--color-text)';
    // Blue accent for subtitle text
    const blueAccent = mode === 'dark' ? "#38bdf8" : '#5E95ED'; ; // sky-400
    // #5E95ED
  const secondaryColor = mode === 'dark' ? '#9ca3af' : '#6b7280'; // gray-400 dark / gray-500 light

  const ragItems: RagItem[] = [
    {
      key: "Everyday Insights",
      label: "Everyday Insights AI",
      subtitle: "Global repository of market information, competitive intel and GMC customer research.",
      icon: (
        <ChatBubbleIcon
          width={32}
          height={32}
          style={{ color: textColor }}
        />
      ),
    },
    {
      key: "Legal",
      label: "Legal",
      subtitle: "Legal resources and general guidance",
      icon: (
        <FileTextIcon
          width={32}
          height={32}
          style={{ color: textColor }}
        />
      ),
    },
    {
      key: "DevOps",
      label: "DevOps",
      subtitle: "DevOps and IT documentation covering technical and software development questions",
      icon: (
        <Code
          size={32}
          strokeWidth={2}
          style={{ color: textColor }}
        />
      ),
    },
    // {
    //   key: "TEBG",
    //   label: "TEBG",
    //   icon: (
    //     <ChatBubbleIcon
    //       width={32}
    //       height={32}
    //       style={{ color: textColor }}
    //     />
    //   ),
    // },
    // {
    //   key: "ITSM",
    //   label: "ITSM",
    //   icon: (
    //     <ChatBubbleIcon
    //       width={32}
    //       height={32}
    //       style={{ color: textColor }}
    //     />
    //   ),
    // },
    // TODO: JIRA
    // {
    //   key: "JIRA",
    //   label: "JIRA",
    //   subtitle: "Issue tracking and project management support",
    //   icon: React.createElement(SiJira as any, {
    //     size: 32,
    //     style: {
    //       color: textColor,
    //     },
    //   }),
    // },
  ];

  const handleSelect = (ragType: RagChatType) => {
    if (!ragType) return;
    dispatch(clearRagHistory());
    dispatch(clearTranslationData());
    dispatch(clearUploadedFiles());
    dispatch(setRagChatType(ragType));
  };

  return (
    <motion.div
      key="rag-selector"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className={`flex flex-col w-full ${sharedPaddingClass}`}
    >
      {/* Subtitle */}
      <p
        className="mb-6 font-medium text-center"
        style={{
          color: blueAccent,
          fontFamily: "3MCircularTT",
          fontSize: "clamp(0.85rem, 1.2vw, 1.05rem)",
        }}
      >
        Select an AI assistance chat:
      </p>

      {/* RAG buttons grid */}
      <div className="grid grid-cols-1 px-6 sm:grid-cols-2 gap-4 max-w-5xl mx-auto items-stretch">
        {ragItems.map((item, i) => {
          const isLastOdd = ragItems.length % 2 !== 0 && i === ragItems.length - 1;
          return (
            <RagButton
              key={item.key}
              item={item}
              index={i}
              accentHex={accentHex}
              textColor={textColor}
              secondaryColor={secondaryColor}
              isDark={mode === 'dark'}
              onSelect={handleSelect}
              wrapperClassName={isLastOdd ? "sm:col-span-2 sm:w-1/2 sm:mx-auto" : ""}
            />
          );
        })}
      </div>
    </motion.div>
  );
}
